// BrandStepMobileMatrix.jsx
import React, { useEffect, useMemo } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import SelectableCard from "./SelectableCard";

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

// spring for snapping back to center
const SPRING = { type: "spring", stiffness: 420, damping: 40, mass: 0.6 };

// swipe thresholds
const DIST_THRESHOLD = 60;     // px
const VEL_THRESHOLD = 300;     // px/s

const BrandStepMobileMatrix = ({
  items,
  currentPage,
  totalPages,
  canPrev,
  canNext,
  setCurrentPage,
  onSwipeEnd,     // still supported (called after we decide page)
  onBrandClick,
  canShow,
}) => {
  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // horizontal offset while dragging
  const x = useMotionValue(0);

  // When currentPage changes (via arrows or programmatically),
  // give a tiny directional slide so the change feels intentional,
  // then snap back to 0.
  useEffect(() => {
    // direction hint: +1 if moving forward, -1 if moving backward
    const dir = 0; // can’t know previous here; we’ll just do a tiny “pop”
    const from = 0;
    // quick pop in place to keep it centered
    animate(x, from, { ...SPRING });
  }, [currentPage, x]);

  const handleDragEnd = (_evt, info) => {
    const { offset, velocity } = info;
    const dx = offset.x;
    const vx = velocity.x;

    let next = currentPage;

    // swipe left → next (if allowed)
    if ((dx < -DIST_THRESHOLD || vx < -VEL_THRESHOLD) && canNext) {
      next = Math.min(totalPages - 1, currentPage + 1);
    }

    // swipe right → prev (if allowed)
    if ((dx > DIST_THRESHOLD || vx > VEL_THRESHOLD) && canPrev) {
      next = Math.max(0, currentPage - 1);
    }

    if (next !== currentPage) {
      setCurrentPage(next);
    }

    // always recenter the content
    animate(x, 0, SPRING);

    // preserve your original callback if you use it elsewhere
    if (onSwipeEnd) onSwipeEnd(_evt, info);
  };

  return (
    <div className="brand-matrix-wrapper">
      <motion.div
        className="brand-matrix"
        variants={containerVariants}
        initial="hidden"
        animate="show"
        drag="x"
        // allow a free drag (no hard constraints), then we snap back to 0
        style={{ x, touchAction: "pan-y" }}
        dragElastic={0.2}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
      >
        {safeItems.map((item) => (
          <motion.div
            key={item.id}
            variants={cardVariants}
            className="brand-matrix-card"
          >
            <SelectableCard item={item} onClick={onBrandClick} />
          </motion.div>
        ))}
      </motion.div>

      {canShow && (
        <div className="matrix-pager">
          <button
            className="matrix-pager-btn"
            onClick={() =>
              canNext && setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
            }
            disabled={!canNext}
            aria-label="التالي"
          >
            <FiChevronRight size={28} />
          </button>

          <span className="matrix-pager-label">
            {totalPages} / {currentPage + 1}
          </span>

          <button
            className="matrix-pager-btn"
            onClick={() => canPrev && setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={!canPrev}
            aria-label="السابق"
          >
            <FiChevronLeft size={28} />
          </button>
        </div>
      )}
    </div>
  );
};

export default BrandStepMobileMatrix;